import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Grid3x3, LayoutList, Loader2, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { CreateClubModal } from "../../../components/CreateClubModal";
import {
  ClubDiscoveryTable,
  ClubMasonryGrid,
  DiscoverEmptyState,
  DiscoverSkeletons,
  FilterChips,
  TrendingSkeleton,
  TrendingStrip,
} from "../../../components/club/discover";
import { Button, SearchInput, SegmentedControl } from "../../../components/ui";
import { trpc } from "../../../lib/trpc";

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DiscoverClubs() {
  const [search, setSearch] = useState("");
  const [gameSlug, setGameSlug] = useState<string | undefined>();
  const [sort] = useState<"newest" | "most_members">("newest");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const [createClubOpen, setCreateClubOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  // Data queries
  const { data: gamesList } = trpc.game.list.useQuery();
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.club.discover.useInfiniteQuery(
      {
        search: debouncedSearch || undefined,
        limit: 24,
        gameSlug,
        sort,
      },
      {
        getNextPageParam: (last) => last.nextCursor,
      },
    );

  const clubs = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const myClubIds = useMemo(() => new Set(myClubs?.map((c) => c.id) ?? []), [myClubs]);

  // Join mutation
  const utils = trpc.useUtils();
  const joinMutation = trpc.clubMember.join.useMutation({
    onSuccess: () => {
      void utils.club.getMyClubs.invalidate();
      void utils.club.discover.invalidate();
      void utils.club.get.invalidate();
      void utils.clubMember.list.invalidate();
      toast.success("Joined club!");
    },
    onError: (error) => {
      if (error.data?.code === "CONFLICT") toast.error("You are already a member of this club");
      else if (error.data?.code === "FORBIDDEN")
        toast.error("This club is not accepting new members");
      else toast.error("Failed to join club. Please try again.");
    },
  });

  const joiningId =
    joinMutation.isPending && joinMutation.variables && "clubId" in joinMutation.variables
      ? joinMutation.variables.clubId
      : null;

  const handleJoin = useCallback(
    (clubId: string) => {
      joinMutation.mutate({ clubId });
    },
    [joinMutation],
  );

  const showSkeleton = isLoading && clubs.length === 0;

  return (
    <div className="relative max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="relative flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight text-white leading-none uppercase">
            Discover Clubs
          </h1>
          <p className="text-xs text-surface-500 mt-1.5">Find your next team. Climb the ranks.</p>
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder="Search clubs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            loading={isFetching && !!search}
            className="w-full sm:w-64"
          />
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="size-4" />}
            className="shrink-0"
            onClick={() => setCreateClubOpen(true)}
          >
            Create Club
          </Button>
        </div>
      </div>

      {/* Accent line */}
      <div className="h-px bg-gradient-to-r from-brand-500/25 via-surface-700/40 to-transparent mb-6" />

      {/* Filter chips */}
      <FilterChips games={gamesList ?? []} gameSlug={gameSlug} onGameChange={setGameSlug} />

      {/* Trending Strip */}
      {showSkeleton ? (
        <TrendingSkeleton />
      ) : clubs.length > 0 && !debouncedSearch ? (
        <TrendingStrip clubs={clubs} myClubIds={myClubIds} />
      ) : null}

      {/* View mode toggle + item count */}
      {clubs.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SegmentedControl
              size="sm"
              options={[
                { value: "table", label: <LayoutList className="size-3.5" /> },
                { value: "cards", label: <Grid3x3 className="size-3.5" /> },
              ]}
              value={viewMode}
              onChange={(v) => setViewMode(v as "table" | "cards")}
            />
            <span className="text-xs text-surface-500 font-medium tabular-nums">
              {clubs.length} clubs
            </span>
          </div>
        </div>
      )}

      {/* Main content */}
      {showSkeleton ? (
        <DiscoverSkeletons viewMode={viewMode} />
      ) : clubs.length === 0 ? (
        <DiscoverEmptyState search={debouncedSearch} onCreateClub={() => setCreateClubOpen(true)} />
      ) : viewMode === "table" ? (
        <ClubDiscoveryTable
          clubs={clubs}
          myClubIds={myClubIds}
          onJoin={handleJoin}
          joiningId={joiningId}
        />
      ) : (
        <ClubMasonryGrid
          clubs={clubs}
          myClubIds={myClubIds}
          onJoin={handleJoin}
          joiningId={joiningId}
        />
      )}

      {/* Load More */}
      {hasNextPage && (
        <div className="flex justify-center pt-8 pb-2">
          <Button
            variant="outline"
            size="lg"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            leftIcon={
              isFetchingNextPage ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ChevronDown className="size-4" />
              )
            }
          >
            {isFetchingNextPage ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}

      <CreateClubModal open={createClubOpen} onClose={() => setCreateClubOpen(false)} />
    </div>
  );
}

export const Route = createFileRoute("/app/clubs/")({
  staticData: { title: "Discover Clubs" },
  component: DiscoverClubs,
});
